import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'hooks/redux'
import {
  getDb35mCode,
  getTruckBuildDate,
  getShowSwitchIndicatorsForVehicle,
} from 'selectors/truck'
import strings from 'utils/strings'
import { ChecTooltip } from 'components/chec'
import { Flex, Icon } from 'components/patterns'
import { classNames } from 'utils/utils'
import { DYNAMIC } from 'constants/featureTypes'
import {
  moduleFeatureResourceCounts,
  findLogicCountConflicts,
} from 'utils/truck'
import {
  not,
  cond,
  always,
  prop,
  T,
  both,
  propEq,
  compose,
  includes,
  __,
} from 'ramda'
import { tooManyResources } from 'i18n/displayConstants/addFeatureToVehicle'
import QPMIcon from 'img/icon_qpm.svg'
import FactoryIcon from 'img/icon_factory_module.svg'
import { ASAM, VECHICLE_TYPE_BUS, XMC1, XMC2 } from 'utils/constants'
import { StyledCurrentLimitWrapper } from './AddRemoveVehicleFeatures.style'
import { exceedsSetBasedLimits } from './utils'
import 'styles/AddRemoveFeaturesTable.css'
import ChecPlusFeaturesAndModulesSection from '../ChecPlusFeaturesAndModulesSection'
import { getModuleCapacity } from '../FeaturesTable/updateEligibility'

const AddRemoveFeaturesTable = (props) => {
  const {
    features,
    onCustomColClick,
    onAddFeature,
    authenticatedModuleNames,
    // handleChangeSwitchSelection,
    canRemoveAsBuiltFeature,
    disableEditing,
    vehicleSubType,
    vehicleECUs = [],
    showDefaultRemove = false,
    hideAlertInfo,
    serialNumber,
    headerAction,
  } = props
  const moduleCounts = useMemo(() => {
    if (!features) return 0

    return moduleFeatureResourceCounts(features)
  }, [features])

  const vehicle35mCode = useSelector(getDb35mCode, { serialNumber })
  const buildDate = useSelector(getTruckBuildDate, { serialNumber })
  const showSwitchIndicators = useSelector(getShowSwitchIndicatorsForVehicle, {
    serialNumber,
  })

  const renderCheckboxCol = (_, record) => {
    const { isMarkedForDeletion, isMarkedForAddition, id } = record

    const isDynamic = propEq('type', DYNAMIC)
    const moduleCapacity = vehicleECUs.find(
      ({ id }) => id === record.moduleName
    )
    const { isOverQueryCount, isOverTimerCount } = findLogicCountConflicts(
      record,
      moduleCounts,
      moduleCapacity
    )

    const cannotAddOrUpdate =
      isOverQueryCount ||
      isOverTimerCount ||
      exceedsSetBasedLimits(record, features, vehicleECUs)

    const cannotRemoveAsBuilt = both(
      propEq('asBuilt', true),
      always(not(canRemoveAsBuiltFeature))
    )

    const cannotRemove = compose(
      not,
      includes(__, authenticatedModuleNames),
      prop('moduleName')
    )

    const tooltipTextGenerator = cond([
      [cannotRemove, always(strings.not_authorized_to_remove_module)],
      [
        both(always(cannotAddOrUpdate), prop('isMarkedForDeletion')),
        always(
          tooManyResources(
            record,
            isOverQueryCount,
            isOverTimerCount,
            getModuleCapacity(record, vehicleECUs)
          )
        ),
      ],
      [prop('isMarkedForDeletion'), always(strings.add_feature_back_tooltip)],
      [cannotRemoveAsBuilt, always(strings.factory_defined_content_tooltip)],
      [
        propEq('isMarkedForUpdate', true),
        always(strings.remove_updated_feature),
      ],
      [
        isDynamic,
        always(strings.dynamic_feature_tooltip_text_add_remove_features),
      ],
      [
        propEq('asBuilt', true),
        always(strings.remove_feature_as_built_tooltip),
      ],
      [T, always(strings.remove_feature_tooltip)],
    ])

    const iconProps = () => {
      if (isDynamic(record)) {
        return {
          icon: 'tooltip-question-circle',
          fill: 'filled',
        }
      }

      if (cannotAddOrUpdate && isMarkedForDeletion && !showDefaultRemove) {
        return {
          icon: 'add_circle',
          fill: 'slateDisabled',
        }
      }
      if (
        cannotRemove(record) ||
        cannotRemoveAsBuilt(record) ||
        isDynamic(record)
      ) {
        return {
          icon: 'info-circle',
          style: {
            color: '#8c96a2',
            fontSize: '20px',
          },
          hoverColor: '#1e7aff',
          fill: 'filled',
        }
      }

      if (isMarkedForDeletion && !showDefaultRemove) {
        return {
          icon: 'plus-circle',
          style: {
            color: '#0b2e6b',
            fontSize: '20px',
          },
          fill: 'filled',
          hoverFill: 'outlined',
        }
      }

      return {
        icon: 'plus-circle',
        style: {
          color: '#4f5764',
          fontSize: '20px',
          transform: 'rotate(45deg)',
        },
        fill: 'outlined',
        hoverFill: 'filled',
      }
    }

    const stateIndicatorClassNames = classNames('state-indicator', {
      'to-be-deleted': isMarkedForDeletion,
      'to-be-added': isMarkedForAddition,
    })

    const handleClick = () => {
      if (
        cannotRemove(record) ||
        (cannotAddOrUpdate && isMarkedForDeletion) ||
        isDynamic(record)
      )
        return null
      const callback = not(isMarkedForDeletion)
        ? null
        : () => onAddFeature(record)

      onCustomColClick(id, isMarkedForDeletion, callback)
      return null
    }

    const addOrRemove =
      isMarkedForDeletion && showDefaultRemove ? null : (
        <IconWithControlledTooltip
          tooltipText={tooltipTextGenerator(record)}
          iconProps={iconProps()}
          onClick={handleClick}
        />
      )

    // Bug #505985 / AB#505985: this column shows only the add/remove control. The
    // orange per-row update icon and its Update/Keep/Remove OverflowMenu, added by
    // PBI 233301, were removed here — pending updates are flagged by the MoreInfo
    // warning triangle, and updating is done through the section header's "Update
    // Feature Versions" button (FeatureVersionUpdateModal). Restoring the per-row
    // affordance means reconciling it with that header workflow, so start from the
    // PBI 233301 discussion rather than from `git show 2c906e743e`.
    const renderIcon = addOrRemove

    return (
      <>
        <div className={stateIndicatorClassNames} />
        <StyledCurrentLimitWrapper>
          {!disableEditing && renderIcon}
        </StyledCurrentLimitWrapper>
      </>
    )
  }

  if (!features) return null

  const sorted = features.sort((a, b) => {
    if (a.isMarkedForAddition || a.isMarkedForDeletion) return -1
    if (b.isMarkedForAddition || b.isMarkedForDeletion) return 1

    // Elevate features with pending updates
    const aHasUpdate =
      a.latestOnVehicle === false ||
      (a.isFeatureLatestRevision === false &&
        a.revisions &&
        a.revisions.length > 0) ||
      (a.status && a.status.toLowerCase() === 'obsolete')
    const bHasUpdate =
      b.latestOnVehicle === false ||
      (b.isFeatureLatestRevision === false &&
        b.revisions &&
        b.revisions.length > 0) ||
      (b.status && b.status.toLowerCase() === 'obsolete')
    if (aHasUpdate && !bHasUpdate) return -1
    if (bHasUpdate && !aHasUpdate) return 1

    return 0
  })

  const modulesFilterCondition = (moduleName) =>
    ![XMC1, ASAM, XMC2].includes(moduleName)

  const qpmFeatures = sorted.filter((item) =>
    modulesFilterCondition(item.moduleName)
  )

  const factoryFeatures = sorted.filter(
    (item) => !modulesFilterCondition(item.moduleName)
  )

  const isSchoolBusVehicle = vehicleSubType === VECHICLE_TYPE_BUS

  return (
    <Flex flexDirection="column" gap="25px">
      <Flex flexDirection="column" gap="16px">
        <ChecPlusFeaturesAndModulesSection
          sectionTitle={
            isSchoolBusVehicle
              ? strings.school_bus_modules_title
              : strings.qpm_modules_title
          }
          sectionSubTitle={
            isSchoolBusVehicle
              ? strings.school_bus_modules_subtitle
              : strings.qpm_modules_subtitle(vehicle35mCode)
          }
          headerAction={
            typeof headerAction === 'function'
              ? headerAction('qpm')
              : headerAction
          }
          features={qpmFeatures}
          vehicleECUs={vehicleECUs.filter((item) =>
            modulesFilterCondition(item.id)
          )}
          icon={QPMIcon}
          tableProps={{
            isEditable: true,
            renderCustomCol: renderCheckboxCol,
            isQPM: true,
            highlightOverLimts: true,
            hideAlertInfo,
            showSwitchIndicators,
          }}
          isQPM
          vehicleSubType={vehicleSubType}
          vehicle35mCode={vehicle35mCode}
          buildDate={buildDate}
        />
      </Flex>
      <Flex flexDirection="column" gap="16px">
        <ChecPlusFeaturesAndModulesSection
          sectionTitle={strings.factory_modules_title}
          sectionSubTitle={strings.factory_modules_subtitle}
          headerAction={
            typeof headerAction === 'function'
              ? headerAction('factory')
              : headerAction
          }
          features={factoryFeatures}
          vehicleECUs={vehicleECUs.filter(
            (item) => !modulesFilterCondition(item.id)
          )}
          icon={FactoryIcon}
          tableProps={{
            isEditable: true,
            renderCustomCol: renderCheckboxCol,
            hideAlertInfo,
          }}
          vehicleSubType={vehicleSubType}
          vehicle35mCode={vehicle35mCode}
          buildDate={buildDate}
        />
      </Flex>
    </Flex>
  )
}

AddRemoveFeaturesTable.propTypes = {
  features: PropTypes.array.isRequired,
  onCustomColClick: PropTypes.func,
  onAddFeature: PropTypes.func,
  canRemoveAsBuiltFeature: PropTypes.bool,
  disableEditing: PropTypes.bool,
  authenticatedModuleNames: PropTypes.array,
  handleChangeSwitchSelection: PropTypes.func,
  vehicleSubType: PropTypes.string,
  vehicleECUs: PropTypes.array,
  showDefaultRemove: PropTypes.bool,
  hideAlertInfo: PropTypes.bool,
  serialNumber: PropTypes.string,
  headerAction: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
}

export default AddRemoveFeaturesTable

const IconWithControlledTooltip = ({ tooltipText, iconProps, onClick }) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false)

  const handleClick = () => {
    setIsTooltipVisible(false)
    onClick()
  }

  return (
    <ChecTooltip
      overlayClassName="long-message-tooltip"
      title={tooltipText}
      visible={isTooltipVisible}
      onVisibleChange={setIsTooltipVisible}
      placement="right"
    >
      <Icon {...iconProps} onClick={handleClick} />
    </ChecTooltip>
  )
}

IconWithControlledTooltip.propTypes = {
  tooltipText: PropTypes.string,
  iconProps: PropTypes.object,
  onClick: PropTypes.func,
}
